/**
 * LinguistPro Transcription Store
 * Centralised state management using Zustand
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AppStep, TranscriptionState, TranscriptionStore, TranscriptUtterance, LogType, VideoSceneDescription } from '../types';
import { SYSTEM_STATUS, SystemStatusType } from '../config/constants';
import { getDefaultModel } from '../config/models';

const initialState: TranscriptionState = {
  step: AppStep.IDLE,
  progress: 0,
  systemStatus: SYSTEM_STATUS.NOMINAL,
  error: null,
  logs: [],
  finalTranscript: '',
  intelligence: '',
  speakerMap: '',
  selectedModelId: getDefaultModel().id,
  suggestedSpeakerNames: {},
  visualIdPending: false,
  confidence: null,
  quality_grade: null,
  utterances: null,
  intelligenceOutputs: {},
  savedTranscriptionId: null,
  // Plan 6: TTS + Voice Q&A (ephemeral — not persisted)
  ttsAudioUrls: {},
  ttsLoading: {},
  sceneDescriptions: null,
};

export const useTranscriptionStore = create<TranscriptionStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setStep: (step: AppStep) => set({ step }, false, 'setStep'),

        setProgress: (progress: number) => set({ progress }, false, 'setProgress'),

        setSystemStatus: (status: SystemStatusType) => 
          set({ systemStatus: status }, false, 'setSystemStatus'),

        setError: (error: string | null) => {
          if (error) {
            set({ error, step: AppStep.ERROR, systemStatus: SYSTEM_STATUS.ERROR }, false, 'setError');
            // NO_BALANCE → open top-up modal without an error toast (SPEC-023)
            if (error === 'NO_BALANCE' && typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('lp:open-top-up'));
            }
          } else {
            set({ error: null }, false, 'clearError');
          }
        },

        addLog: (message: string, type: LogType) => {
          const { logs, systemStatus } = get();
          
          // Determine new system status based on log content
          let newStatus: SystemStatusType = systemStatus;
          if (message.includes('Retrying') || message.includes('Cooling')) {
            newStatus = SYSTEM_STATUS.RETRYING;
          } else if (message.includes('Phase') || message.includes('Segment')) {
            newStatus = SYSTEM_STATUS.PROCESSING;
          }

          const newLog = {
            id: crypto.randomUUID(),
            timestamp: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
            message,
            type,
          };

          // Circular buffer: keep last 50 logs to prevent unbounded growth (SPEC-023)
          set(
            { logs: [...logs.slice(-49), newLog], systemStatus: newStatus },
            false,
            'addLog'
          );
        },

        clearLogs: () => set({ logs: [] }, false, 'clearLogs'),

        setFinalTranscript: (transcript: string) =>
          set({ finalTranscript: transcript }, false, 'setFinalTranscript'),

        setIntelligence: (intel: string) =>
          set({ intelligence: intel }, false, 'setIntelligence'),

        setSpeakerMap: (map: string) =>
          set({ speakerMap: map }, false, 'setSpeakerMap'),

        setSuggestedSpeakerNames: (names: Record<number, string>) =>
          set({ suggestedSpeakerNames: names }, false, 'setSuggestedSpeakerNames'),

        setVisualIdPending: (pending: boolean) =>
          set({ visualIdPending: pending }, false, 'setVisualIdPending'),

        setConfidence: (confidence: number | null) =>
          set({ confidence }, false, 'setConfidence'),

        setQualityGrade: (grade: string | null) =>
          set({ quality_grade: grade }, false, 'setQualityGrade'),

        setUtterances: (utterances: TranscriptUtterance[] | null) =>
          set({ utterances }, false, 'setUtterances'),

        setIntelligenceOutputs: (outputs: Record<string, string>) =>
          set({ intelligenceOutputs: outputs }, false, 'setIntelligenceOutputs'),

        setSavedTranscriptionId: (id: string | null) =>
          set({ savedTranscriptionId: id }, false, 'setSavedTranscriptionId'),

        // ── Plan 6: TTS + Voice Q&A ──────────────────────────────────────────
        setTtsAudio: (tabId: string, audioBase64: string) =>
          set(
            (s) => ({ ttsAudioUrls: { ...s.ttsAudioUrls, [tabId]: audioBase64 } }),
            false,
            'setTtsAudio'
          ),

        setTtsLoading: (tabId: string, loading: boolean) =>
          set(
            (s) => ({ ttsLoading: { ...s.ttsLoading, [tabId]: loading } }),
            false,
            'setTtsLoading'
          ),

        setSceneDescriptions: (scenes: VideoSceneDescription[] | null) =>
          set({ sceneDescriptions: scenes }, false, 'setSceneDescriptions'),

        setSelectedModelId: (modelId: string) =>
          set({ selectedModelId: modelId }, false, 'setSelectedModelId'),

        reset: () => {
          const { selectedModelId } = get(); // Preserve model selection
          const resetLog = {
            id: crypto.randomUUID(),
            timestamp: new Date().toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
            message: 'Session cleared. Ready for the next recording.',
            type: 'warning' as LogType,
          };

          set(
            {
              ...initialState,
              selectedModelId, // Keep the model selection
              logs: [resetLog],
            },
            false,
            'reset'
          );
        },
      }),
      {
        name: 'LinguistPro-Store',
        partialize: (state) => ({ 
          selectedModelId: state.selectedModelId,
        }),
      }
    ),
    { name: 'LinguistPro-Store' }
  )
);

/** Selector hooks for optimised renders */
export const useStep = () => useTranscriptionStore((s) => s.step);
export const useProgress = () => useTranscriptionStore((s) => s.progress);
export const useSystemStatus = () => useTranscriptionStore((s) => s.systemStatus);
export const useError = () => useTranscriptionStore((s) => s.error);
export const useLogs = () => useTranscriptionStore((s) => s.logs);
export const useFinalTranscript = () => useTranscriptionStore((s) => s.finalTranscript);
export const useIntelligence = () => useTranscriptionStore((s) => s.intelligence);
export const useSpeakerMap = () => useTranscriptionStore((s) => s.speakerMap);
export const useSelectedModelId = () => useTranscriptionStore((s) => s.selectedModelId);
export const useQualityGrade = () => useTranscriptionStore((s) => s.quality_grade);
export const useConfidence = () => useTranscriptionStore((s) => s.confidence);
export const useUtterances = () => useTranscriptionStore((s) => s.utterances);
export const useIntelligenceOutputs = () => useTranscriptionStore((s) => s.intelligenceOutputs);
export const useSavedTranscriptionId = () => useTranscriptionStore((s) => s.savedTranscriptionId);
export const useTtsAudioUrls = () => useTranscriptionStore((s) => s.ttsAudioUrls);
export const useTtsLoading = () => useTranscriptionStore((s) => s.ttsLoading);
export const useSceneDescriptions = () => useTranscriptionStore((s) => s.sceneDescriptions);
